/**
 * Words Tab Component
 * Displays word frequency analysis with sortable table
 */

import { ChangeEvent } from 'react'

import {
	Autocomplete,
	Chip,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
} from '@mui/material'

import MDBox from 'components/MDBox'
import MDInput from 'components/MDInput'
import MDTypography from 'components/MDTypography'

import { WordsTabProps } from './types'

const TOP_WORDS_OPTIONS = [10, 15, 20, 30, 50]

function WordsTab({
	words,
	maxWordCount,
	searchFilter,
	onSearchFilterChange,
	topWordsCount,
	onTopWordsCountChange,
	sortField,
	sortDirection,
	onSort,
}: WordsTabProps): JSX.Element {
	return (
		<MDBox>
			<MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
				<MDTypography variant="h6">Word Frequency Analysis</MDTypography>
				<MDBox display="flex" gap={2}>
					<MDInput
						placeholder="Search words..."
						size="small"
						value={searchFilter}
						onChange={(e: ChangeEvent<HTMLInputElement>) => onSearchFilterChange(e.target.value)}
						sx={{ width: 200 }}
					/>
					<Autocomplete
						disableClearable
						value={topWordsCount}
						options={TOP_WORDS_OPTIONS}
						getOptionLabel={option => `Top ${option}`}
						onChange={(_, newValue) => onTopWordsCountChange(newValue)}
						size="small"
						sx={{ width: 120 }}
						renderInput={params => <MDInput {...params} label="Count" />}
					/>
				</MDBox>
			</MDBox>

			<TableContainer>
				<Table size="small" sx={{ tableLayout: 'fixed', width: '100%', minWidth: 650 }}>
					<TableHead sx={{ display: 'table-header-group', width: '100%' }}>
						<TableRow sx={{ display: 'table-row', width: '100%' }}>
							<TableCell sx={{ width: '5%' }} align="center">
								#
							</TableCell>
							<TableCell sx={{ width: '25%' }} align="left">
								<TableSortLabel
									active={sortField === 'word'}
									direction={sortField === 'word' ? sortDirection : 'asc'}
									onClick={() => onSort('word')}
								>
									Word
								</TableSortLabel>
							</TableCell>
							<TableCell sx={{ width: '15%' }} align="center">
								<TableSortLabel
									active={sortField === 'count'}
									direction={sortField === 'count' ? sortDirection : 'asc'}
									onClick={() => onSort('count')}
								>
									Count
								</TableSortLabel>
							</TableCell>
							<TableCell sx={{ width: '15%' }} align="center">
								<TableSortLabel
									active={sortField === 'percentage'}
									direction={sortField === 'percentage' ? sortDirection : 'asc'}
									onClick={() => onSort('percentage')}
								>
									Percentage
								</TableSortLabel>
							</TableCell>
							<TableCell sx={{ width: '40%' }} align="left">
								Distribution
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{words.map((word, index) => {
							const barWidth = (word.count / maxWordCount) * 100
							return (
								<TableRow key={word.word} hover>
									<TableCell sx={{ width: '5%' }} align="center">
										<MDTypography variant="caption" color="text">
											{index + 1}
										</MDTypography>
									</TableCell>
									<TableCell sx={{ width: '25%' }} align="left">
										<MDTypography variant="button" fontWeight="medium">
											{word.word}
										</MDTypography>
									</TableCell>
									<TableCell sx={{ width: '15%' }} align="center">
										<Chip label={word.count} size="small" color="info" />
									</TableCell>
									<TableCell sx={{ width: '15%' }} align="center">
										<MDTypography variant="caption">{word.percentage?.toFixed(2)}%</MDTypography>
									</TableCell>
									<TableCell sx={{ width: '40%' }} align="left">
										<MDBox
											sx={{
												width: `${barWidth}%`,
												height: 8,
												backgroundColor: 'info.main',
												borderRadius: 1,
												minWidth: 4,
											}}
										/>
									</TableCell>
								</TableRow>
							)
						})}
					</TableBody>
				</Table>
			</TableContainer>
		</MDBox>
	)
}

export default WordsTab
